import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';

import { useGetUsersByAccess } from '@frontend/features/users/queries';

import {
  ALL_BOOKING_LIST_SCOPE,
  BookingListScope,
  MINE_BOOKING_LIST_SCOPE,
  isBookingListAdvisorId,
} from '../bookingListScope';

interface Props {
  agencyId: number;
  scope: BookingListScope;
  onChange: (scope: BookingListScope) => void;
}

interface AdvisorOption {
  id: string;
  label: string;
}

/**
 * DE QUIÉN son las reservas que se están mirando. Es un selector y no un
 * filtro más: condiciona toda la pantalla, contadores de pestañas incluidos,
 * y por eso vive arriba de todo y no entre los chips.
 *
 * El maestro de usuarios se consulta SOLO cuando el asesor abre el
 * autocomplete: la lista no hace falta para entrar a la pantalla, y pedirla
 * siempre sería una consulta por visita para un caso que casi nadie usa.
 */
export function BookingListScopeSelector({ agencyId, scope, onChange }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const { data, isFetching } = useGetUsersByAccess({
    // Sin agencia el hook queda deshabilitado: es la puerta para no pedir el
    // maestro hasta que se lo necesita.
    agencyId: open ? String(agencyId) : undefined,
    search,
    pageSize: 20,
  });

  const options: AdvisorOption[] = (data?.items ?? [])
    .filter((user) => isBookingListAdvisorId(user.id))
    .map((user) => ({
      id: user.id,
      label:
        [user.firstName, user.lastName].filter(Boolean).join(' ').trim() ||
        user.email,
    }));

  return (
    <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
      <ToggleButtonGroup
        size="small"
        exclusive
        value={scope.kind === 'advisor' ? null : scope.kind}
        onChange={(_event, value) => {
          if (value === 'mine') onChange(MINE_BOOKING_LIST_SCOPE);
          if (value === 'all') onChange(ALL_BOOKING_LIST_SCOPE);
        }}
        aria-label={t('travelServices.bookingList.scope.label')}
      >
        <ToggleButton value="mine">
          {t('travelServices.bookingList.scope.mine')}
        </ToggleButton>
        <ToggleButton value="all">
          {t('travelServices.bookingList.scope.all')}
        </ToggleButton>
      </ToggleButtonGroup>

      <Autocomplete<AdvisorOption, false, false, false>
        size="small"
        sx={{ width: 240 }}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        options={options}
        loading={isFetching}
        filterOptions={(all) => all}
        // El usuario elegido se muestra desde el ámbito, no desde la lista:
        // el rótulo se guardó al elegirlo, así que sigue ahí sin volver a
        // pedir el maestro.
        value={
          scope.kind === 'advisor' && scope.advisor
            ? { id: scope.advisor.id, label: scope.advisor.label }
            : null
        }
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onInputChange={(_event, value, reason) => {
          if (reason === 'input') setSearch(value);
        }}
        onChange={(_event, value) =>
          // Limpiar el asesor no acota a nadie: vuelve al ámbito más ancho.
          // Caer en "Mías" escondería trabajo que el asesor no pidió esconder.
          onChange(
            value
              ? { kind: 'advisor', advisor: { id: value.id, label: value.label } }
              : ALL_BOOKING_LIST_SCOPE,
          )
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('travelServices.bookingList.scope.advisor')}
            placeholder={t('travelServices.bookingList.scope.advisorPlaceholder')}
          />
        )}
      />
    </Box>
  );
}
